import { useEffect, useState } from 'react';
import { Popconfirm } from 'antd';
import { CheckOutlined, EditOutlined } from '@ant-design/icons';

import { useAppSelector } from 'app/store';
import { useLazyConsultationsQuery, useTakeToWorkConsultationMutation } from 'app/services/edpApiV1/consultations';
import { useSubordinatesQuery } from 'app/services/edpApiV1/viewer';

import { viewerModel } from 'entities/viewer';
import { dataForConsultationsTable, sortingDate } from 'shared/helpers/utils';
import { useNotifications } from 'shared/hooks/use-notifications';

import { RequestsSearch, TColumns, TConsultant } from 'shared/types/types';
import { TablePull } from 'features/tables';
import { usePullSort } from 'shared/hooks';
import { RolesUser, SortCondition } from 'shared/enums';
import { Link } from 'react-router-dom';
import { useIndicators } from 'features/indicators';
import { ONE_MINUTE } from 'shared/constants';
import { TConsultationsPreparedData } from 'shared/types/consultations.types';
import { SearchKeysNews } from 'shared/enums/news';
import { getAllAvailableOrganizations } from 'entities/organizations/model/organizations';
import ModalConsultationsSubordinates from '../modalConsultationsSubordinates';
import style from './index.module.scss';
import { IConsultationsPullTableRecord } from './types';

const isConsultant = (record: IConsultationsPullTableRecord): record is TConsultationsPreparedData =>
  'consultant' in record && record.consultant != null;

export const ConsultationsPull = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentSize, setCurrentSize] = useState<number>(10);
  const [dataConsultations, setDataConsultations] = useState<TConsultationsPreparedData[] | []>([]);
  const [consultantIdCurrent, setConsultantIdCurrent] = useState<number>();
  const [openModal, setOpenModal] = useState(false);
  const [requestId, setRequestId] = useState<number>();
  const [sortInfo, sortTrigger] = usePullSort();
  const [search, setSearch] = useState<RequestsSearch>({ byId: undefined, fio: undefined });
  const allAvailableOrganizations = useAppSelector(getAllAvailableOrganizations);

  const accountInfo = useAppSelector(viewerModel.me.selectors.selectViewerProfile);

  const isConsultantMe = (record: IConsultationsPullTableRecord) => {
    return isConsultant(record) && accountInfo?.id === record.consultant?.id;
  };

  const [trigger, result] = useTakeToWorkConsultationMutation();
  const { data: allSubordinates } = useSubordinatesQuery({ include: 'me' });
  const sortOption = sortInfo.value && sortInfo.value === SortCondition.ASCEND ? SortCondition.ASC : SortCondition.DESC;

  const [getConsultations, { data, isSuccess: resultConsultationsSuccess, isFetching: resultConsultationsFetching }] =
    useLazyConsultationsQuery({
      pollingInterval: ONE_MINUTE,
    });

  useEffect(() => {
    getConsultations({
      type: 'new',
      size: currentSize,
      page: currentPage,
      order: sortInfo.value ? sortOption : undefined,
      allAvailableOrganizations,
      ...search,
    });
  }, [currentPage, currentSize, getConsultations, search, sortInfo.value, sortOption, allAvailableOrganizations]);

  const { isSuccess, isError, reset } = result;

  const handleChangeResponsible = (id: number, consultantId?: number): void => {
    setRequestId(id);
    setOpenModal(true);
    setConsultantIdCurrent(consultantId);
  };

  const handleToWork = (id: string | number): void => {
    trigger(Number(id));
  };

  const columns: TColumns = [
    {
      title: '№',
      dataIndex: 'id',
      key: 'key',
      width: 140,
    },
    {
      title: 'Дата',
      dataIndex: 'created',
      key: 'created',
      sortDirections: [SortCondition.ASCEND, SortCondition.DESCEND],
      sorter: (a, b) => sortingDate(a.created, b.created),
      onHeaderCell: () => ({
        onClick: () => sortTrigger(),
      }),
    },
    {
      title: 'Тема',
      dataIndex: 'topic',
      key: 'topic',
    },
    {
      title: 'ФИО',
      dataIndex: 'fio',
      key: 'fio',
    },
    {
      title: 'Должность',
      dataIndex: 'position',
      key: 'position',
    },
    {
      title: 'Подразделение',
      dataIndex: 'division',
      key: 'division',
    },
    {
      title: 'Дан ответ',
      dataIndex: 'isHRReplied',
      key: 'isHRReplied',
      width: 102,
      render: (isHRReplied: boolean) => {
        if (isHRReplied) {
          return <CheckOutlined style={{ color: 'green', fontSize: '20px', width: '100%', textAlign: 'center' }} />;
        }
      },
    },
    {
      title: 'Действие',
      dataIndex: 'actions',
      key: 'actions',
      render: (_, record) => {
        if (isConsultantMe(record as TConsultationsPreparedData)) {
          return <Link to={`/consultations/${record.id}`}>Ответить</Link>;
        }
        if (isConsultant(record as TConsultationsPreparedData)) {
          return <Link to={`/consultations/${record.id}`}>Посмотреть</Link>;
        }
        return (
          <Popconfirm placement="leftTop" title="Вы уверены?" onConfirm={() => handleToWork(record.key)}>
            <a>Взять в работу</a>
          </Popconfirm>
        );
      },
    },
  ];

  const columnsWithSubordinates: TColumns = [...columns];

  if (!allAvailableOrganizations && accountInfo.role === RolesUser.HRDirector) {
    columnsWithSubordinates.push({
      title: 'Ответственный',
      dataIndex: 'consultant',
      key: 'consultant',
      render: (consultant: TConsultant, record) => {
        return isConsultantMe(record as TConsultationsPreparedData) ? null : (
          <>
            {consultant && (consultant.name || consultant.middleName || consultant.lastName) ? (
              <div className={style.editConsultant}>
                <span>{`${consultant.lastName} ${consultant.name} ${consultant.middleName ?? ''}`}</span>
                <EditOutlined
                  className={style.btnChangeResponsible}
                  onClick={() => handleChangeResponsible(Number(record.key), consultant.id)}
                />
              </div>
            ) : (
              <span className={style.changeResponsible} onClick={() => handleChangeResponsible(Number(record.key))}>
                Назначить
              </span>
            )}
          </>
        );
      },
    });
  }

  useEffect(() => {
    if (data?.data) {
      const dataTable = dataForConsultationsTable(data.data);
      if (dataTable) {
        setDataConsultations(dataTable);
      }
    }
  }, [data]);

  useEffect(() => {
    const paginationInfo = data?.pagination;
    if (dataConsultations?.length === 0 && paginationInfo && currentPage !== 1) {
      // определяю текущую страницу
      const totalPages = Math.ceil(paginationInfo.total / paginationInfo.size);
      //  устанавливает текущую страницу на минимальное значение из текущей страницы и общего количества страниц,
      //  чтобы избежать установки текущей страницы на значение, превышающее общее количество страниц.
      setCurrentPage(Math.min(currentPage, totalPages));
    }
  }, [dataConsultations, data]);

  // подписываемся на обновление индикаторов, после обновления списка и отключаем пуллинг звпроса индикаторов
  useIndicators(resultConsultationsSuccess, resultConsultationsFetching, false);
  useNotifications(isSuccess, isError, 'Ответственный назначен', 'Что-то пошло не так', reset);

  const onSearch = (data: string, key: SearchKeysNews) => {
    if (key === SearchKeysNews.SEARCH_ID) {
      setSearch({ fio: undefined, byId: data ? data : undefined });
    } else if (key === SearchKeysNews.SEARCH_NAME) {
      setSearch({ byId: undefined, fio: data ? data : undefined });
    } else {
      setSearch({ [key]: data ? data : undefined });
    }
  };
  return (
    <>
      {dataConsultations && (
        <TablePull
          dataSource={dataConsultations}
          columns={
            accountInfo?.role === RolesUser.HRDirector || accountInfo?.role === RolesUser.SecondLine
              ? columnsWithSubordinates
              : columns
          }
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          currentSize={currentSize}
          setCurrentSize={setCurrentSize}
          total={data?.pagination?.total}
          onSearch={onSearch}
        />
      )}
      {allSubordinates && allSubordinates.length > 0 && requestId && (
        <ModalConsultationsSubordinates
          allSubordinates={allSubordinates}
          openModal={openModal}
          setOpenModal={setOpenModal}
          requestId={requestId}
          consultantIdCurrent={consultantIdCurrent}
        />
      )}
    </>
  );
};
